import { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { BoxButton, MainTitle, useDialog, useToast } from '@repo/ui';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BankCode } from '@/__generated__/checkBankAccountMutation.graphql';
import { simpleApplyMutation$variables } from '@/__generated__/simpleApplyMutation.graphql';
import BankAccountForm from '@/components/simple-terms/Forms/BankAccountForm';
import PhoneNumberForm from '@/components/simple-terms/Forms/PhoneNumberForm';
import { TermsForm } from '@/components/simple-terms/Forms/TermsForm';
import { AUTO_LOGIN_CODE_KEY } from '@/components/tax-refund/common/data/auth-info';
import { TAX_REFUND_ERROR } from '@/components/tax-refund/common/error/error';
import checkBankMaintenanceTime from '@/components/tax-refund/common/utils/checkBankMaintenanceTime';
import { CheckAccountNameDrawerView } from '@/components/tax-refund/common/view/BankAccountView/CheckAccountNameDrawerView';
import { INFOTECH_BANK } from '@/lib/constants/bank';
import { SIMPLE_TERMS_PATHS } from '@/lib/constants/paths';
import { useCheckTokenAndBankAccount } from '@/lib/hooks/api/mutation/use-check-token-and-bank-account';
import { useSimpleApply } from '@/lib/hooks/api/mutation/use-simple-apply';
import { simpleApplyFormSchema } from '@/lib/regex/schema/simple-apply';

export const SimpleTermsContent = ({ corpType }: { corpType: 'indis' | 'corps' }) => {
  const checkAccountNameDrawerView = useModal(CheckAccountNameDrawerView);
  const router = useRouter();

  const methods = useForm({
    resolver: yupResolver(simpleApplyFormSchema)
  });
  const {
    getValues,
    watch,
    setError,
    formState: { isValid }
  } = methods;

  const { toast } = useToast();
  const { showDialog } = useDialog();
  const watchBankId = watch('bankId');
  const watchAccountNumber = watch('accountNumber');

  const [verifiedBankAccountName, setVerifiedBankAccountName] = useState('');
  const { commit: checkBankAccount, isInFlight: isCheckBankAccountLoading } = useCheckTokenAndBankAccount();
  const { commit: simpleApply, isInFlight: isSimpleApplyLoading } = useSimpleApply();

  const { bankName, bankIconFileName } = useMemo(
    () => INFOTECH_BANK.find(({ bankId }) => bankId === watchBankId) || { bankName: '농협은행', bankIconFileName: 'logo_nh' },
    [watchBankId]
  );

  const handleSimpleApply = (data: simpleApplyMutation$variables) => {
    simpleApply({
      variables: {
        ...data
      },
      onCompleted: (response, errors) => {
        if (errors) {
          console.log(errors);
          if (errors.length > 0) {
            toast({
              title: errors[0]?.message,
              variant: 'warning'
            });
          }
          return;
        }

        if (response?.simpleApply?.errors?.code === TAX_REFUND_ERROR.중복신청) {
          toast({
            title: '이미 환급을 신청하셨네요. 결과는 신청했던 계정으로 확인해주세요.',
            variant: 'warning'
          });
        }

        if (response?.simpleApply?.result) {
          if (corpType === 'indis') {
            router.push({
              pathname: SIMPLE_TERMS_PATHS.개인신청성공,
              query: router?.query
            });
          } else {
            router.push({
              pathname: SIMPLE_TERMS_PATHS.법인신청성공,
              query: router?.query
            });
          }
        }
      },
      onError: (error: Error) => {
        console.log(error);
        toast({
          title: error.message,
          variant: 'warning'
        });
      }
    });
  };
  const handleSimpleApplyWithBankAccount = () => {
    const { accountNumber, phone } = getValues();
    handleSimpleApply({
      phoneNumber: phone.trim().replaceAll('-', ''),
      bank: bankName,
      accountOwner: verifiedBankAccountName,
      accountNo: accountNumber,
      code: sessionStorage.getItem(AUTO_LOGIN_CODE_KEY) ?? ''
    });
    checkAccountNameDrawerView.hide();
  };

  const handleCheckBankAccount = () => {
    const { bankId, accountNumber } = getValues();
    checkBankAccount({
      variables: {
        bankCode: bankId as BankCode,
        account: accountNumber ?? '',
        code: sessionStorage.getItem(AUTO_LOGIN_CODE_KEY) ?? ''
      },

      onCompleted: (response, errors) => {
        if (errors) {
          console.log(errors);
          if (errors.length > 0) {
            toast({
              title: errors[0]?.message,
              variant: 'warning'
            });
          }
          return;
        }

        if (response?.checkBankAccountByCode?.errors?.code === TAX_REFUND_ERROR.유효시간만료된URL) {
          router.push({
            pathname: SIMPLE_TERMS_PATHS.토큰유효시간만료,
            query: router.query
          });
        }

        if (response?.checkBankAccountByCode?.result) {
          const bizName = corpType === 'indis' ? response.checkBankAccountByCode.taxpayerName : response.checkBankAccountByCode.hometaxBizName;
          setVerifiedBankAccountName(response?.checkBankAccountByCode?.name ?? '');
          checkAccountNameDrawerView.show({
            onClickYes: handleSimpleApplyWithBankAccount,
            accountName: response?.checkBankAccountByCode?.name ?? '',
            account: {
              bankId: watchBankId ?? '',
              accountNumber: watchAccountNumber ?? ''
            },
            bankName,
            bankIconFileName,
            title: `${corpType === 'corps' ? `'${bizName}'의` : `${bizName}님의`} 계좌가 맞나요?`
          });
        } else {
          const isBankMaintenanceTime = checkBankMaintenanceTime();
          showDialog({
            title: '계좌를 다시 입력해 주세요',
            description: `존재하지 않는 계좌번호예요. 계좌번호를 다시 확인해주세요.${isBankMaintenanceTime ? '\n\n*23:50~24:30 사이에는 은행망 점검으로 인하여 계좌 조회 오류가 발생할 수 있어요.' : ''}`
          });
          setError('accountNumber', {
            message: '존재하지 않는 계좌번호예요. 계좌번호를 다시 확인해주세요.'
          });
        }
      },
      onError: (err: Error) => {
        console.log(err);
        toast({
          title: '계좌 실명 확인 중 에러가 발생하였습니다.',
          variant: 'warning'
        });
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <div className="container-medium container-padding-medium pb-spacious-medium">
        <MainTitle title={`가장 간편하게\n환급신청을 완료해보세요`} size="medium" />
        <PhoneNumberForm />
        <BankAccountForm />
        <TermsForm />
        <BoxButton className={'w-full'} disabled={!isValid} isLoading={isCheckBankAccountLoading || isSimpleApplyLoading} onClick={handleCheckBankAccount}>
          환급 신청하기
        </BoxButton>
      </div>
    </FormProvider>
  );
};
